import React, { useState, useRef, useEffect } from 'react';
import '../../styles/EssayDetector.css';
import { motion } from 'framer-motion';
import {
  Container, CssBaseline, IconButton, TextField, Stack, Avatar, Paper,
  CircularProgress, InputAdornment, Box, Typography, GlobalStyles
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import ImageIcon from '@mui/icons-material/Image';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import MuiTooltip from '@mui/material/Tooltip';

import { Doughnut } from 'react-chartjs-2';
import { Chart, ArcElement, Tooltip, Legend } from 'chart.js';
import { auth, db } from '../../config/firebase';
import { onAuthStateChanged } from 'firebase/auth';
import { collection, query, where, getDocs } from 'firebase/firestore';

// Subcomponents
import Navbar from './Navbar';
import History from './History';
import GenerateId from './GenerateId';
import ProgressBar from './ProgressBar';

Chart.register(ArcElement, Tooltip, Legend);

const BotAvatar = () => (
  <Avatar alt="Bot" src="/static/images/avatar/bot.jpg" sx={{ width: 48, height: 48 }} />
);

const UserAvatar = () => (
  <Avatar alt="User" src="/static/images/avatar/user.jpg" sx={{ width: 48, height: 48 }} />
);

const EssayMessage = ({ avatar, message }) => (
  <Stack direction="row" spacing={2} alignItems="flex-start" sx={{ mb: 2 }}>
    {avatar}
    <Paper
      elevation={0}
      sx={{
        padding: '16px 24px',
        borderRadius: '16px',
        backgroundColor: '#1E1E1E',
        color: '#E0E0E0',
        width: 'fit-content',
        maxWidth: { xs: '95%', md: '85%' }, // Adjust message width on small screens
        wordWrap: 'break-word',
        lineHeight: 1.6,
      }}
    >
      <Typography variant="body1">{message}</Typography>
    </Paper>
  </Stack>
);

const EssayDetector = () => {
  const [essayInput, setEssayInput] = useState('');
  const [tokenInput, setTokenInput] = useState('');
  const [progressInput, setProgressInput] = useState(0);
  const [output, setOutput] = useState([]);
  const [accessToken, setAccessToken] = useState('');
  const [doughnutChartData, setDoughnutChartData] = useState({
    labels: ['AI', 'Human'],
    datasets: [{
      label: 'AI vs Human',
      data: [50, 50],
      backgroundColor: ['#7e5add', '#FFFFFF'], 
      borderWidth: 0,
      borderRadius: 12, 
      spacing: 6,       
      hoverOffset: 6
    }]
  });
  const [verdict, setVerdict] = useState('Awaiting Input...');
  const [accuracyScore, setAccuracyScore] = useState(0);
  const [username, setUsername] = useState('');
  const [loading, setLoading] = useState(false);
  
  const chatContainerRef = useRef(null);

  useEffect(() => {
    const fetchUsername = async (email) => {
      const usersRef = collection(db, 'users');
      const q = query(usersRef, where('email', '==', email));
      const querySnapshot = await getDocs(q);
      
      if (!querySnapshot.empty) {
        setUsername(querySnapshot.docs[0].id);
      } else {
        console.error('No user found with the given email');
      }
    };

    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        fetchUsername(user.email);
        const idToken = await user.getIdToken();
        setAccessToken(idToken);
      } else {
        setUsername('');
      }
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, [output, loading]);

  const processResponse = (data) => {
    setDoughnutChartData(prevData => ({
      ...prevData,
      datasets: [{
        ...prevData.datasets[0],
        data: [data.stats.ai, data.stats.human]
      }]
    }));

    setProgressInput(data.stats.ai);
    setVerdict(data.verdict);
    setAccuracyScore(data.stats.ai);
    setTokenInput(data.text);

    const colorizedSentences = data.sentences.map((sentence, index) => (
      <span key={index} style={{ color: sentence.result === 'AI-generated text' ? '#7e5add' : '#E0E0E0' }}>
        {sentence.text}{' '}
      </span>
    ));

    setOutput(prev => [...prev, { type: 'bot', message: <>{colorizedSentences}</> }]);
    setEssayInput('');
  };

  const handleSubmit = async () => {
    if (!essayInput.trim()) return;
    
    setOutput(prev => [...prev, { type: 'user', message: essayInput }]);
    setLoading(true); 

    try {
      const response = await fetch('https://parentproxy.pythonanywhere.com/classify/essay', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`
        },
        body: JSON.stringify({ essay: essayInput }),
      });
      const data = await response.json();
      processResponse(data);
    } catch (error) {
      console.error('Error fetching essay analysis:', error);
    } finally {
      setLoading(false); 
    }
  };

  const handleFileChange = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setLoading(true); 
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await fetch('https://craigdsouza2.pythonanywhere.com/upload/essay', {
        method: 'POST',
        body: formData,
      });
      const data = await response.json();
      processResponse(data);
    } catch (error) {
      console.error('Error uploading file:', error);
    } finally {
      setLoading(false); 
    }
  };

  const handleImageChange = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setLoading(true); 
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await fetch('https://parentproxy.pythonanywhere.com/ocr/essay', {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${accessToken}` },
        body: formData,
      });
      const data = await response.json();
      processResponse(data);
    } catch (error) {
      console.error('Error uploading image:', error);
    } finally {
      setLoading(false); 
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', backgroundColor: '#121212' }}>
      <CssBaseline />
      <GlobalStyles styles={{
        '*::-webkit-scrollbar': {
          width: '8px',
          height: '8px',
        },
        '*::-webkit-scrollbar-track': {
          backgroundColor: '#1A1A1A',
        },
        '*::-webkit-scrollbar-thumb': {
          backgroundColor: '#7e5add',
          borderRadius: '10px',
          border: '2px solid #1A1A1A',
        },
        '*::-webkit-scrollbar-thumb:hover': {
          backgroundColor: '#6c4bc8',
        }
      }} />
      <Navbar />
      
      <Container 
        maxWidth="xl" 
        sx={{ 
          display: 'flex', 
          flexDirection: { xs: 'column', lg: 'row' }, // Stacks on mobile/tablet, side-by-side on desktop
          flexGrow: 1, 
          overflow: { xs: 'auto', lg: 'hidden' },     // Allows scrolling on mobile if components stack
          gap: 3, 
          py: { xs: 1, md: 3 },
          px: { xs: 1, sm: 2, md: 3, lg: 4 }
        }}
      >
        {/* HISTORY SECTION - Hidden on smaller screens */}
        <Box
          component={motion.div}
          initial={{ opacity: 0, x: -50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5 }}
          sx={{ 
            display: { xs: 'none', lg: 'block' }, // <--- Responsive hiding applied here
            flex: 1, 
            backgroundColor: '#1E1E1E', 
            borderRadius: 4, 
            p: 2, 
            overflowY: 'auto',
            border: '1px solid #333'
          }}
        >
          <Typography variant="h6" sx={{ color: '#fff', mb: 2, fontWeight: 600, textAlign: 'center' }}>
            History
          </Typography>
          <History username={username} />
        </Box>

        {/* CHAT SECTION - Always visible, takes full width on mobile */}
        <Box sx={{ flex: 2, display: 'flex', flexDirection: 'column', minHeight: { xs: '70vh', lg: 'auto' } }}>
          <Box 
            ref={chatContainerRef} 
            sx={{ 
              flex: 1, 
              overflowY: 'auto', 
              backgroundColor: '#121212',
              mb: 2, 
              pr: { xs: 0, sm: 2 }
            }}
          >
            {output.length === 0 && (
              <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', textAlign: 'center', px: 2 }}>
                <Typography variant="body1" sx={{ color: '#666' }}>Type an essay or upload a file to begin analysis.</Typography>
              </Box>
            )}
            
            {output.map((essay, index) => (
              <EssayMessage
                key={index}
                avatar={essay.type === 'user' ? <UserAvatar /> : <BotAvatar />}
                message={essay.message}
              />
            ))}
            {loading && <CircularProgress size={30} sx={{ color: '#7e5add', mt: 2, ml: 2 }} />}
          </Box>

          <Box sx={{ backgroundColor: '#1E1E1E', p: { xs: 1, sm: 2 }, borderRadius: 4, border: '1px solid #333' }}>
            <TextField
              fullWidth
              multiline
              maxRows={4}
              placeholder="Paste text to analyze..."
              value={essayInput}
              onChange={(e) => setEssayInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSubmit(); } }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  color: '#fff',
                  '& fieldset': { border: 'none' },
                  padding: { xs: '8px', sm: '16.5px 14px' } // tighter padding on mobile
                }
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end" sx={{ gap: { xs: 0, sm: 1 } }}>
                    <input accept="application/pdf" style={{ display: 'none' }} id="upload-pdf" type="file" onChange={handleFileChange} />
                    <MuiTooltip title="Upload PDF">
                      <label htmlFor="upload-pdf">
                        <IconButton component="span" sx={{ color: '#888', '&:hover': { color: '#fff' } }}>
                          <PictureAsPdfIcon />
                        </IconButton>
                      </label>
                    </MuiTooltip>

                    <input accept="image/*" style={{ display: 'none' }} id="upload-image" type="file" onChange={handleImageChange} />
                    <MuiTooltip title="Upload Image (OCR)">
                      <label htmlFor="upload-image">
                        <IconButton component="span" sx={{ color: '#888', '&:hover': { color: '#fff' } }}>
                          <ImageIcon />
                        </IconButton>
                      </label>
                    </MuiTooltip>

                    <IconButton onClick={handleSubmit} disabled={!essayInput.trim()} sx={{ color: '#7e5add', '&:disabled': { color: '#444' } }}>
                      <SendIcon />
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />
          </Box>

          <Box sx={{ mt: 2 }}>
            <ProgressBar bgcolor="#7e5add" completed={progressInput * 100} />
            <GenerateId username={username} recentText={tokenInput} accuracyScore={accuracyScore} />
          </Box>
        </Box>

        {/* RESULTS SECTION - Hidden on smaller screens */}
        <Box
          component={motion.div}
          initial={{ opacity: 0, x: 50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5 }}
          sx={{ 
            display: { xs: 'none', lg: 'block' }, // <--- Responsive hiding applied here
            flex: 1 
          }}
        >
          <Box sx={{ 
            backgroundColor: '#1E1E1E', 
            borderRadius: 4, 
            border: '1px solid #333',
            p: 4, 
            height: '100%', 
            display: 'flex', 
            flexDirection: 'column',
            alignItems: 'center'
          }}>
            <Typography variant="overline" sx={{ color: '#888', letterSpacing: 2, mb: 3 }}>
              Analysis Results
            </Typography>

            <Box component={motion.div} whileHover={{ scale: 1.05 }} sx={{ width: '80%', mb: 4 }}>
              <Doughnut 
                data={doughnutChartData} 
                options={{ 
                  cutout: '45%', 
                  plugins: { 
                    legend: { 
                      labels: { color: '#fff' } 
                    } 
                  } 
                }} 
              />
            </Box>

            <Typography variant="overline" sx={{ color: '#888', letterSpacing: 2 }}>
              Final Verdict
            </Typography>
            <Typography variant="h5" sx={{ color: '#fff', fontWeight: 600, mt: 1, mb: 3, textAlign: 'center' }}>
              {verdict}
            </Typography>
            
            <Typography variant="h2" sx={{ color: '#7e5add', fontWeight: 700 }}>
              {accuracyScore ? `${(accuracyScore * 100).toFixed(1)}%` : '--%'}
            </Typography>
            <Typography variant="caption" sx={{ color: '#888', letterSpacing: 1, textTransform: 'uppercase', mt: 1 }}>
              AI Probability Score
            </Typography>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default EssayDetector;